import { useState } from "react";
import { X, MapPin, Phone, ExternalLink, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { resolveEventColor } from "@/lib/eventColor";
import { StatusBadge, JobStatus } from "@/components/StatusBadge";
import { type AssignedStaffMember } from "@/lib/calendarSpans";
import { StaffAssignmentPanel } from "./StaffAssignmentPanel";
import { useStaffEventsData } from "@/contexts/StaffEventsContext";

interface EventDetailsEvent {
  id: string;
  jobNumber?: string;
  title: string;
  customer: string;
  address?: string;
  phone?: string;
  date: Date;
  endDate?: Date;
  status: JobStatus;
  assignedStaff?: AssignedStaffMember[];
  conflictColor?: string;
  statusColor?: string;
  jobTypeColor?: string;
  providerColor?: string;
  source?: string;
  highestConflictSeverity?: string;
  description?: string;
}

interface EventDetailsCardProps {
  event: EventDetailsEvent;
  onClose: () => void;
  onOpenJob?: (jobId: string) => void;
  onReassignStaff?: (jobId: string) => void;
  onNavigate?: (address: string) => void;
  onCallCustomer?: (phone: string) => void;
  allStaff?: any[];
  allEvents?: any[];
}

export function EventDetailsCard({ 
  event, 
  onClose,
  onOpenJob,
  onReassignStaff,
  onNavigate,
  onCallCustomer,
  allStaff,
  allEvents,
}: EventDetailsCardProps) {
  const [assignOpen, setAssignOpen] = useState(false);
  const { staff: contextStaff, events: contextEvents } = useStaffEventsData();
  
  // Use provided props if available, otherwise use context data
  const staff = allStaff || contextStaff;
  const events = allEvents || contextEvents;
  
  const eventColor = resolveEventColor(event);

  return (
    <div 
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl p-6 shadow-xl border border-[var(--wd-gray-200)] w-[420px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold">{event.title}</h2>
            {event.jobNumber && (
              <div className="text-sm text-muted-foreground mt-1">{event.jobNumber}</div>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="flex-shrink-0"
            data-testid="button-close-details"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Status Badge */}
        <div className="mb-4">
          <StatusBadge status={event.status} />
          
          {/* Conflict warning if present */}
          {event.conflictColor && event.highestConflictSeverity && (
            <Badge variant="destructive" className="ml-2">
              Conflict: {event.highestConflictSeverity}
            </Badge>
          )}
        </div>

        {/* Time Range */}
        <div className="mb-4 p-3 rounded-md bg-muted/50">
          <div className="text-sm font-medium mb-1">Scheduled Time</div>
          <div className="text-sm">
            {event.date.toLocaleString('en-AU', { 
              weekday: 'short',
              day: 'numeric', 
              month: 'short', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
          {event.endDate && event.endDate.getTime() !== event.date.getTime() && (
            <div className="text-sm text-muted-foreground">
              to {event.endDate.toLocaleString('en-AU', { 
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
        </div>

        {/* Customer */}
        <div className="mb-4">
          <div className="text-sm font-medium mb-1">Customer</div>
          <div className="text-sm">{event.customer}</div>
        </div>

        {/* Address */}
        {event.address && (
          <div className="mb-4">
            <div className="text-sm font-medium mb-1">Address</div>
            <div className="text-sm text-muted-foreground">{event.address}</div>
          </div>
        )}

        {/* Phone */}
        {event.phone && (
          <div className="mb-4">
            <div className="text-sm font-medium mb-1">Phone</div>
            <div className="text-sm text-muted-foreground">{event.phone}</div>
          </div>
        )}

        {/* Description */}
        {event.description && (
          <div className="mb-4">
            <div className="text-sm font-medium mb-1">Description</div>
            <div className="text-sm text-muted-foreground">{event.description}</div>
          </div>
        )}

        {/* Assigned Staff */}
        {event.assignedStaff && event.assignedStaff.length > 0 && (
          <div className="mb-4">
            <div className="text-sm font-medium mb-2">Assigned Crew</div>
            <div className="flex gap-2 flex-wrap">
              {event.assignedStaff.map((staff) => (
                <Badge 
                  key={staff.id} 
                  variant="secondary"
                  className="px-2 py-1"
                >
                  <Users className="h-3 w-3 mr-1" />
                  {staff.firstName} {staff.lastName}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Calendar Source */}
        {event.source && event.source !== 'workdeck' && (
          <div className="mb-4">
            <div className="text-sm font-medium mb-1">Source</div>
            <Badge variant="outline" className="capitalize">
              {event.source}
            </Badge>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-6 space-y-2">
          {onOpenJob && (
            <Button 
              className="w-full" 
              onClick={() => onOpenJob(event.id)}
              data-testid="button-open-job"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Job
            </Button>
          )}

          {onReassignStaff && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setAssignOpen(true)}
              data-testid="button-reassign-staff"
            >
              <Users className="h-4 w-4 mr-2" />
              Reassign Staff
            </Button>
          )}

          {event.address && onNavigate && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => onNavigate(event.address!)}
              data-testid="button-navigate"
            >
              <MapPin className="h-4 w-4 mr-2" />
              Navigate to Address
            </Button>
          )}

          {event.phone && onCallCustomer && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => onCallCustomer(event.phone!)}
              data-testid="button-call-customer"
            >
              <Phone className="h-4 w-4 mr-2" />
              Call Customer
            </Button>
          )}
        </div>
      </div>

      {/* Staff Assignment Panel */}
      <StaffAssignmentPanel
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        staff={staff}
        selectedStaffIds={event.assignedStaff?.map(s => s.id) || []}
        date={event.date}
        events={events}
        onSave={(newStaffIds) => {
          // Call the global reassign function if available
          if (typeof window !== 'undefined' && (window as any).WORKDECK_REASSIGN) {
            (window as any).WORKDECK_REASSIGN(event.id, newStaffIds);
          }
          // Also call the callback if provided
          if (onReassignStaff) {
            onReassignStaff(event.id);
          }
        }}
      />
    </div>
  );
}
